import { ReactNode } from "react";

import {
  CheckCircleFill,
  Clock,
  DotsCircleFill,
  PauseCircleFill,
  PlayCircleFill,
  StopCircleFill,
  ArrowCircleDown,
  PencilCircleFill,
} from "@hl/base-components/lib/assets/icons.generated";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { Text, BadgeProps, Box, Group, BoxProps } from "@mantine/core";
import dayjs, { extend } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  BlockchainEntity,
  BlockchainEntityStatus,
} from "components/BlockchainEntityTitle/types";

extend(relativeTime);

const BaseBadge = ({
  icon,
  color,
  subtitle,
  children,
  ...rest
}: {
  icon?: ReactNode;
  color?: string;
  subtitle?: string;
  children: ReactNode;
} & BadgeProps) => {
  return (
    <Box
      sx={(theme) => ({
        position: "absolute",
        top: 8,
        left: 8,
        zIndex: 1,
        borderRadius: "999px",
        height: "24px",
        padding: "4px 4px",
        backgroundColor: theme.colors.baseBackground[0],
        outline: `1px solid ${theme.colors.divider[0]}`,
      })}
      {...rest}
    >
      <Group spacing={5} h="100%">
        {icon && (
          <Text lh={0} color={color}>
            {icon}
          </Text>
        )}
        <Group spacing={4}>
          <Text lh={icon ? 0 : 1.3} size="xs" weight={500} color={color}>
            {children}
          </Text>
          {subtitle && (
            <>
              <Text lh={icon ? 0 : 1.33} size="xs" color={color}>
                ·
              </Text>
              <Text lh={icon ? 0 : 1.33} size="xs" color={color}>
                {subtitle}
              </Text>
            </>
          )}
        </Group>
      </Group>
    </Box>
  );
};

const getBadge = (blockchainEntity: BlockchainEntity) => {
  let entity;
  switch (blockchainEntity.status) {
    case BlockchainEntityStatus.DRAFT:
      return (
        <BaseBadge icon={<PencilCircleFill />} color={TEXT_COLOR.SECONDARY}>
          Draft
        </BaseBadge>
      );

    case BlockchainEntityStatus.DEPLOYING:
      return <BaseBadge color={TEXT_COLOR.SECONDARY}>Deploying...</BaseBadge>;

    case BlockchainEntityStatus.MINT_NOT_STARTED:
    case BlockchainEntityStatus.AUCTION_NOT_STARTED:
      entity =
        blockchainEntity.status === BlockchainEntityStatus.MINT_NOT_STARTED
          ? "Mint"
          : "Auction";
      return (
        <BaseBadge icon={<Clock />}>
          {blockchainEntity.start
            ? `${entity} starts ${daysFromNow(blockchainEntity.start)}`
            : `${entity} not started`}
        </BaseBadge>
      );

    case BlockchainEntityStatus.MINT_STARTED:
    case BlockchainEntityStatus.AUCTION_IN_PROGRESS:
      entity =
        blockchainEntity.status === BlockchainEntityStatus.MINT_STARTED
          ? "Mint"
          : "Auction";
      return (
        <BaseBadge
          color={STATUS_COLOR.AFFIRMATIVE}
          icon={<PlayCircleFill />}
          subtitle={
            blockchainEntity.end
              ? `Ends ${daysFromNow(blockchainEntity.end)}`
              : undefined
          }
        >
          {`${entity} active`}
        </BaseBadge>
      );

    case BlockchainEntityStatus.MINT_PAUSED:
      return (
        <BaseBadge icon={<PauseCircleFill />} color={TEXT_COLOR.SECONDARY}>
          Mint paused
        </BaseBadge>
      );

    case BlockchainEntityStatus.MINT_SOLD_OUT:
      return (
        <BaseBadge icon={<CheckCircleFill />} color={TEXT_COLOR.SECONDARY}>
          Sold out
        </BaseBadge>
      );

    case BlockchainEntityStatus.MINT_ENDED:
    case BlockchainEntityStatus.AUCTION_ENDED_RESERVE_NOT_MET:
      return (
        <BaseBadge
          color={TEXT_COLOR.SECONDARY}
          icon={<StopCircleFill />}
          subtitle={
            blockchainEntity.status ===
            BlockchainEntityStatus.AUCTION_ENDED_RESERVE_NOT_MET
              ? "Reserve not met"
              : undefined
          }
        >
          {`${
            blockchainEntity.status === BlockchainEntityStatus.MINT_ENDED
              ? "Mint"
              : "Auction"
          } ended`}
        </BaseBadge>
      );

    case BlockchainEntityStatus.AUCTION_ENDED_NEEDS_FULFILLMENT:
    case BlockchainEntityStatus.AUCTION_ENDED_FULFILLED:
      return (
        <BaseBadge
          color={TEXT_COLOR.SECONDARY}
          icon={
            blockchainEntity.status ===
            BlockchainEntityStatus.AUCTION_ENDED_NEEDS_FULFILLMENT ? (
              <DotsCircleFill />
            ) : (
              <CheckCircleFill />
            )
          }
          subtitle={
            blockchainEntity.status ===
            BlockchainEntityStatus.AUCTION_ENDED_NEEDS_FULFILLMENT
              ? "Needs fulfillment"
              : "Fulfilled"
          }
        >
          Auction ended
        </BaseBadge>
      );

    case BlockchainEntityStatus.IMPORTED:
      return <BaseBadge icon={<ArrowCircleDown />}>Imported</BaseBadge>;

    default:
      return <BaseBadge color={TEXT_COLOR.SECONDARY}>Unknown</BaseBadge>;
  }
};

const StatusBadge = ({
  blockchainEntity,
  children,
  ...rest
}: {
  blockchainEntity: BlockchainEntity;
  children: ReactNode;
} & BoxProps) => {
  return (
    <Box
      sx={{
        position: "relative",
      }}
      {...rest}
    >
      {getBadge(blockchainEntity)}
      {children}
    </Box>
  );
};

const daysFromNow = (date: string) => dayjs(date).fromNow();

export default StatusBadge;
